import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Download, Building2, TrendingUp, TrendingDown, Minus, AlertTriangle, Star } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useToast } from '@/components/ui/MobileToast'
import { businessService, RedFlagItem } from '@/services/businessService'
import { generateBusinessReport } from '@/utils/reportGenerator'

export function BusinessDetailPage() {
  const { businessId } = useParams<{ businessId: string }>()
  const navigate = useNavigate()
  const showToast = useToast()
  const [isLoading, setIsLoading] = useState(true)
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false)
  const [businessData, setBusinessData] = useState<any>(null)
  const [scoreHistory, setScoreHistory] = useState<any[]>([])
  const [redFlags, setRedFlags] = useState<RedFlagItem[]>([])
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'alerts'>('overview')

  useEffect(() => {
    loadBusinessData()
  }, [businessId])

  const loadBusinessData = async () => {
    if (!businessId) return
    
    setIsLoading(true)
    try {
      const [business, historyResponse, redFlagsResponse] = await Promise.all([
        businessService.getBusinessById(businessId),
        businessService.getScoreHistory(businessId, 12),
        businessService.getRedFlags(businessId, 'active')
      ])
      
      setBusinessData(business)
      setScoreHistory(historyResponse.history || [])
      setRedFlags(redFlagsResponse.redFlags || [])
    } catch (error) {
      console.error('Failed to load business data:', error)
      showToast.showToast({
        type: 'error',
        title: 'Load Failed',
        message: 'Unable to load business details.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleDownloadReport = async () => {
    if (!businessData) return
    
    setIsGeneratingPDF(true)
    try {
      await generateBusinessReport(businessData, scoreHistory, redFlags)
      showToast.showToast({
        type: 'success',
        title: 'Report Downloaded',
        message: 'Business report has been downloaded successfully.'
      })
    } catch (error) {
      console.error('Failed to generate report:', error)
      showToast.showToast({
        type: 'error',
        title: 'Download Failed',
        message: 'Unable to generate report. Please try again.'
      })
    } finally {
      setIsGeneratingPDF(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!businessData) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Business Not Found
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          The requested business could not be found.
        </p>
        <button
          onClick={() => navigate('/lender/search')}
          className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium"
        >
          Back to Search
        </button>
      </div>
    )
  }

  const score = parseFloat(businessData.score || '0')
  const band = businessData.band || 'E'
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {businessData.business_name}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {businessData.industry_sector || 'Unknown Industry'}
            </p>
          </div>
        </div>
        
        <button
          onClick={handleDownloadReport}
          disabled={isGeneratingPDF}
          className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium disabled:opacity-50"
        >
          {isGeneratingPDF ? (
            <LoadingSpinner size="sm" />
          ) : (
            <>
              <Download className="w-4 h-4 mr-2" />
              Download Report
            </>
          )}
        </button>
      </div>


      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900 dark:text-white">
              {score.toFixed(2)}
            </div>
            <div className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium mt-1 ${
              band === 'S' || band === 'A' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
              band === 'B' || band === 'C' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
              'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
            }`}>
              {band}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              TLBH Score
            </div>
          </div>
          
          <div className="text-center">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              band === 'S' || band === 'A' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
              band === 'B' || band === 'C' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
              'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
            }`}>
              {band === 'S' || band === 'A' ? 'Low' : band === 'B' || band === 'C' ? 'Medium' : 'High'} Risk
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Risk Level
            </div>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center space-x-1">
              {scoreHistory.length >= 2 && scoreHistory[0].score > scoreHistory[scoreHistory.length - 1].score ? (
                <TrendingUp className="w-4 h-4 text-green-600" />
              ) : scoreHistory.length >= 2 && scoreHistory[0].score < scoreHistory[scoreHistory.length - 1].score ? (
                <TrendingDown className="w-4 h-4 text-red-600" />
              ) : (
                <Minus className="w-4 h-4 text-gray-600" />
              )}
              <span className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                {scoreHistory.length >= 2 && scoreHistory[0].score > scoreHistory[scoreHistory.length - 1].score ? 'Improving' :
                 scoreHistory.length >= 2 && scoreHistory[0].score < scoreHistory[scoreHistory.length - 1].score ? 'Declining' : 'Stable'}
              </span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Trend
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {parseFloat(businessData.confidence || '85').toFixed(0)}%
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Confidence
            </div>
          </div>
        </div>
      </div>


      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex px-6">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'history', label: 'History' },
              { id: 'alerts', label: 'Alerts', count: redFlags.length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-3 px-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {tab.label}
                {tab.count !== undefined && tab.count > 0 && (
                  <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                    activeTab === tab.id
                      ? 'bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-400'
                      : 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-300'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Business Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">PAN:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-mono">{businessData.pan}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">GSTIN:</span>
                      <span className="ml-2 text-gray-900 dark:text-white font-mono">{businessData.gstin || 'N/A'}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Business Type:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{businessData.business_type}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Industry:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{businessData.industry_sector || 'N/A'}</span>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Registration Date:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">
                        {new Date(businessData.registration_date).toLocaleDateString('en-IN')}
                      </span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Email:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{businessData.contact_email || 'N/A'}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Phone:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{businessData.contact_phone || 'N/A'}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Address:</span>
                      <span className="ml-2 text-gray-900 dark:text-white">{businessData.registered_address || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}


          {activeTab === 'history' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Score History (Last 12 Months)
              </h3>
              {scoreHistory.length > 0 ? (
                <div className="space-y-4">
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                    <div className="space-y-2">
                      {scoreHistory.map((item, index) => {
                        const prevScore = index < scoreHistory.length - 1 ? scoreHistory[index + 1].score : item.score
                        const change = item.score - prevScore
                        
                        return (
                          <div key={item.id} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                            <div className="flex items-center space-x-3">
                              <div className="text-sm text-gray-600 dark:text-gray-400 w-32">
                                {new Date(item.timestamp).toLocaleDateString('en-IN', { 
                                  year: 'numeric', 
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                  {item.score.toFixed(2)}
                                </span>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  item.band === 'S' || item.band === 'A' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                                  item.band === 'B' || item.band === 'C' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                                  'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                }`}>
                                  {item.band}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              {change !== 0 && (
                                <div className={`flex items-center space-x-1 text-sm ${
                                  change > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                                }`}>
                                  {change > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                                  <span>{Math.abs(change).toFixed(2)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {scoreHistory[0]?.score.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Current Score
                      </div>
                    </div>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {(scoreHistory.reduce((sum, item) => sum + item.score, 0) / scoreHistory.length).toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Average Score
                      </div>
                    </div>
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 text-center">
                      <div className={`text-2xl font-bold ${
                        scoreHistory[0]?.score > scoreHistory[scoreHistory.length - 1]?.score 
                          ? 'text-green-600 dark:text-green-400' 
                          : scoreHistory[0]?.score < scoreHistory[scoreHistory.length - 1]?.score
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        {scoreHistory.length >= 2 
                          ? (scoreHistory[0].score - scoreHistory[scoreHistory.length - 1].score).toFixed(2)
                          : '0.00'}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        12-Month Change
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 dark:text-gray-400">
                    No historical data available.
                  </p>
                </div>
              )}
            </div>
          )}
          {activeTab === 'alerts' && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Active Alerts & Red Flags
              </h3>
              {redFlags.length > 0 ? (
                <div className="space-y-3">
                  {redFlags.map((flag) => {
                    const getSeverityColor = (impact: number) => {
                      if (impact >= 15) return 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20'
                      if (impact >= 10) return 'border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-900/20'
                      return 'border-yellow-300 dark:border-yellow-700 bg-yellow-50 dark:bg-yellow-900/20'
                    }
                    
                    const getSeverityText = (impact: number) => {
                      if (impact >= 15) return 'Critical'
                      if (impact >= 10) return 'High'
                      if (impact >= 5) return 'Medium'
                      return 'Low'
                    }
                    
                    return (
                      <div key={flag.id} className={`border rounded-lg p-4 ${getSeverityColor(flag.scoreImpact)}`}>
                        <div className="flex items-start space-x-3">
                          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                                {flag.category.replace(/_/g, ' ')}
                              </h4>
                              <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                                flag.scoreImpact >= 15 ? 'bg-red-200 text-red-800 dark:bg-red-800 dark:text-red-200' :
                                flag.scoreImpact >= 10 ? 'bg-orange-200 text-orange-800 dark:bg-orange-800 dark:text-orange-200' :
                                'bg-yellow-200 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-200'
                              }`}>
                                {getSeverityText(flag.scoreImpact)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                              {flag.condition}
                            </p>
                            {flag.actionRequired && (
                              <div className="mt-3 p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600">
                                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                                  Recommended Action:
                                </p>
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                  {flag.actionRequired}
                                </p>
                              </div>
                            )}
                            <div className="flex items-center space-x-4 mt-3 text-xs text-gray-600 dark:text-gray-400">
                              <span className="font-medium">Impact: -{flag.scoreImpact} points</span>
                              <span>Detected: {formatDate(flag.detectedAt)}</span>
                              <span className={`px-2 py-0.5 rounded ${
                                flag.status === 'active' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                                'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                              }`}>
                                {flag.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Star className="w-8 h-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No Active Alerts
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    This business has a clean record with no active red flags or alerts.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
